import { TABLET_PORTRAIT } from '@tovia/man-ui';
import React from 'react';
import { SplashConfig } from 'src/@types/DefaultConfig';
import { DESKTOP, XX_LARGE } from 'src/client/constants/mediaQueries';
import { useSelector } from 'src/client/redux';
import styled from 'styled-components/macro';
import VisibilitySensor from 'react-visibility-sensor';
import { usePromotions } from 'src/client/helpers/usePromotions';
import { PromoBanner } from 'src/client/containers/Promo/PromoBanner';
import { usePlayboyLandingPage } from 'src/client/helpers/usePlayboyLandingPage';
import { TEXT_SHADOW_SMALL } from './styles';
import { useMediaUrl } from './useMediaUrls';

type Props = {
  setSplashAdVisible: (visible: boolean) => void;
};

export const VideoIntro = ({ setSplashAdVisible }: Props) => {
  const { splash } = useSelector((state) => state.app.config);
  const { isPromoBannerActive } = usePromotions();
  const showPlayboyBanner = usePlayboyLandingPage();
  const mediaUrl = useMediaUrl();

  const splashIntroComponent =
    splash?.components?.introComponent || ({} as SplashConfig['components']['introComponent']);

  if (isPromoBannerActive && !showPlayboyBanner) {
    return (
      <>
        <VisibilitySensor onChange={setSplashAdVisible} partialVisibility>
          <PromoBanner />
        </VisibilitySensor>
        <IntroTitle>{splashIntroComponent.title}</IntroTitle>
      </>
    );
  }

  return (
    <Wrapper>
      <BackgroundVideo autoPlay loop muted playsInline>
        <source
          src={`${mediaUrl}/desktop-hero.mp4`}
          // Safari >17.5 has a bug looping .webm, the codec here should only work in Safari
          type={'video/mp4; codecs="hvc1"'}
          media="(min-width: 768px)"
        />
        <source src={`${mediaUrl}/desktop-hero.webm`} type="video/webm" media="(min-width: 768px)" />
        <source src={`${mediaUrl}/desktop-hero.mp4`} type="video/mp4" media="(min-width: 768px)" />
        <source src={`${mediaUrl}/mobile-hero.mp4`} type="video/mp4" media="(max-width: 767px)" />
      </BackgroundVideo>
      <CenterContainer>
        <IntroTitle>{splashIntroComponent.title}</IntroTitle>
        <IntroMessage>{splashIntroComponent.message}</IntroMessage>
      </CenterContainer>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  aspect-ratio: 5 / 9;
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media ${TABLET_PORTRAIT} {
    aspect-ratio: 9 / 5;
  }
`;

const CenterContainer = styled.div`
  position: absolute;
  text-align: center;
`;

const IntroTitle = styled.h1`
  font-weight: 800;
  font-size: 20px;
  color: ${(props) => props.theme.primaryBtn1Bg};
  text-shadow: 0px 0px 20px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
  text-align: center;

  @media ${TABLET_PORTRAIT} {
    font-size: 26px;
  }

  @media ${DESKTOP} {
    font-size: 38px;
  }

  @media ${XX_LARGE} {
    font-size: 64px;
  }
`;

const IntroMessage = styled.div`
  font-size: 14px;
  line-height: 1.2;
  ${TEXT_SHADOW_SMALL};
  color: #ffffff;

  @media ${DESKTOP} {
    font-size: 18px;
  }

  @media ${XX_LARGE} {
    font-size: 24px;
  }
`;

export const BackgroundVideo = styled.video`
  position: absolute;
  object-fit: cover;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;
